import React, {useMemo, useRef} from 'react'
import PropTypes from 'prop-types'
import ModuleContainer from '../../components/cms/module-container'
import StickyStack from '../../components/cms/sticky-stack'
import {Box, useSlotRecipe, Button} from '@chakra-ui/react3'
import PriceButtonGroup from '../../components/cms/price-button-group'

import Image from '../../components/cms/image'
import {useReducedMotion} from 'framer-motion'
import {AddIcon} from '../../components/icons'
import RichText from '../../components/cms/rich-text'
import highlightSlotRecipe from './index.recipe'
import Asset from '../../components/cms/asset'
import {useIntl} from 'react-intl'
import useCTAAction from '../../hooks/useCtaAction'
import {hasValidString} from '../../utils/utils'

/**
 * HighlightsStackModule
 *
 * Renders a stacked set of highlight teasers with sticky scroll animation.
 * Each highlight can render either an `asset` (video/image) or a fallback `image`,
 * plus optional text content and CTA/price block.
 *
 * @component
 *
 * @param {string} [moduleId] - CMS module id.
 * @param {string} [moduleType] - CMS module type (e.g. 'leap-highlight-stacks').
 * @param {string} [pageId] - Page id for tracking.
 * @param {string} [anchor] - Anchor id for in-page navigation.
 * @param {'default'|'subtle'|'strong'|'inverted'} [background='default'] - Module background surface.
 * @param {number} [headingLevel] - Base heading level (Highlight uses `h{headingLevel + 1}`).
 * @param {boolean} [enableAnimation=true] - Enables StickyStack animation (disabled when reduced motion).
 * @param {'product'|'lifestyle'} [variant='product'] - Visual variant used by slot recipe.
 * @param {'0%'|'40%'|'80%'} [darken='0%'] - Darken overlay intensity (used by recipe).
 *
 * @param {Array<HighlightItem>} highlights - List of highlights to render.
 *
 * @returns {JSX.Element}
 */

const Highlight = ({
    subline,
    headline,
    copy,
    priceProduct,
    cta,
    image,
    asset,
    headingLevel,
    trackingContext,
    type,
    overlay,
    variant = 'product',
    desktopImageAlignment = 'right',
    darken = '0%'
}) => {
    const {formatMessage} = useIntl()
    const ctaProps = useCTAAction({
        action: {type: 'overlay', value: overlay}
    })

    const recipe = useSlotRecipe({recipe: highlightSlotRecipe})
    const styles = useMemo(
        () => recipe({variant, darken, desktopImageAlignment}),
        [recipe, variant, darken, desktopImageAlignment]
    )
    const videoRef = useRef(null)

    return (
        <>
            <Box role="group" aria-label={type} css={styles.root}>
                <Box css={styles.widthContainer} layerStyle="container.page">
                    <Box css={styles.contentContainer}>
                        <Box as={`h${headingLevel + 1}`} css={styles.textContainer}>
                            <RichText css={styles.subline}>{subline}</RichText>
                            <RichText css={styles.headline}>{headline}</RichText>
                            {hasValidString(copy?.text) && (
                                <RichText css={styles.copy}>{copy.text}</RichText>
                            )}
                        </Box>
                        {cta?.label && (
                            <Box css={styles.buttons}>
                                <PriceButtonGroup
                                    price={priceProduct}
                                    cta={cta}
                                    size="small"
                                    trackingContext={trackingContext}
                                    variant={variant}
                                />
                            </Box>
                        )}
                    </Box>
                    <Box css={styles.assetContainer}>
                        {asset ? ( //for backward compatibility
                            <Asset
                                asset={asset}
                                videoRef={videoRef}
                                widths={{base: '100vw', s: '941px'}}
                                objectFit="cover"
                                objectPosition="center"
                            />
                        ) : (
                            <Image
                                {...image}
                                widths={{base: '100vw', s: '941px'}}
                                objectFit="cover"
                                objectPosition="center"
                            />
                        )}
                    </Box>
                    <Box css={styles.darkenLayer}></Box>
                </Box>
                {overlay && (
                    <Box css={styles.overlay}>
                        <Button
                            {...ctaProps}
                            size="md"
                            variant="primaryFilled"
                            aria-label={formatMessage({
                                defaultMessage: 'More Information, opens Overlay',
                                id: 'leap.highlights.assitive_msg.more_information'
                            })}
                        >
                            <AddIcon />
                        </Button>
                    </Box>
                )}
            </Box>
        </>
    )
}

const highlightProps = {
    subline: PropTypes.string,
    headline: PropTypes.string,
    type: PropTypes.string,
    copy: PropTypes.object,
    priceProduct: PropTypes.object,
    cta: PropTypes.object,
    image: PropTypes.shape({
        src: PropTypes.string,
        alt: PropTypes.string,
        text: PropTypes.string
    }),
    trackingContext: PropTypes.shape({
        moduleName: PropTypes.string
    }),
    headingLevel: PropTypes.number,
    variant: PropTypes.oneOf(['product', 'lifestyle']),
    desktopImageAlignment: PropTypes.oneOf(['right', 'left']),
    darken: PropTypes.oneOf(['0%', '40%', '80%']),
    asset: PropTypes.object,
    overlay: PropTypes.string
}

Highlight.propTypes = highlightProps

const HighlightsStackModule = React.forwardRef(
    (
        {
            moduleId,
            moduleType,
            pageId,
            anchor,
            background,
            highlights,
            headingLevel,
            enableAnimation,
            variant,
            darken
        },
        ref
    ) => {
        const shouldReduceMotion = useReducedMotion()
        // don't show animation if animation is disabled or motion should be reduced
        const noAnimation = !enableAnimation || shouldReduceMotion

        return (
            <ModuleContainer
                moduleId={moduleId}
                moduleType={moduleType}
                pageId={pageId}
                anchor={anchor}
                background={background}
                spacing="none"
                ref={ref}
                modulePadding="highlightStack"
                layerStyle={null}
            >
                <StickyStack.Container noAnimation={noAnimation}>
                    {highlights.map((highlight, index) => (
                        <StickyStack.Item key={index}>
                            <Highlight
                                headingLevel={headingLevel}
                                variant={variant}
                                darken={darken}
                                {...highlight}
                                trackingContext={{moduleName: moduleType, eventLabel: 'cta'}}
                            />
                        </StickyStack.Item>
                    ))}
                </StickyStack.Container>
            </ModuleContainer>
        )
    }
)

if (process.env.NODE_ENV !== 'production') {
    HighlightsStackModule.displayName = 'HighlightsStackModule'
}

HighlightsStackModule.propTypes = {
    anchor: PropTypes.string,
    background: PropTypes.oneOf(['default', 'subtle', 'strong', 'inverted']),
    headingLevel: PropTypes.number,
    highlights: PropTypes.arrayOf(PropTypes.shape(highlightProps)).isRequired,
    moduleId: PropTypes.string,
    moduleType: PropTypes.string,
    pageId: PropTypes.string,
    enableAnimation: PropTypes.oneOf([true, false]),
    variant: PropTypes.oneOf(['product', 'lifestyle']),
    darken: PropTypes.oneOf(['0%', '40%', '80%'])
}

export default HighlightsStackModule
